/**
 * Time integration.
 *
 * `tick(state, now)` advances the world to `now`. Production is integrated
 * SEGMENT-WISE: the timeline between lastTick and now is cut at every moment
 * something changes the rates (a job completes, a crater expires, an event
 * fires), and each segment is integrated with the rates that were true during
 * it. That is what makes offline progress exact — a mine finished 6 hours into
 * an 8-hour absence produces at its new level for the remaining 2 hours, never
 * for all 8 and never for 0.
 */

import type { BuildingKey, DefenseKey, EngineEvent, GameState, QueueJob, ShipKey, TickResult, UnitKey } from './types.js';
import { BUILDINGS } from './data/buildings.js';
import { RESEARCH } from './data/research.js';
import { DEFENSE_KEYS, UNITS } from './data/units.js';
import { talentEffects } from './data/talents.js';
import { productionReport, setBuildingLevel, storageReport } from './state.js';
import { nextInt, nextRandom } from './rng.js';
import { marketUnlocked, refreshOffers } from './market.js';
import { fleetCargo, raidingParty, refreshCamps, scoutSuccessChance } from './espionage.js';
import { MISSIONS, activeMission } from './missions.js';
import { MODIFIER_LABELS, nextEffectBoundary, pruneEffects } from './modifiers.js';
import { simulateBattle } from './combat.js';
import { grantXp } from './progression.js';
import {
  RAID_POWER_RATIO,
  defenderParty,
  defenderPower,
  plunderAmounts,
  raidLeadTimeMs,
  rollPirateFleet,
} from './raids.js';

/** Deep-clone a serializable state. */
export function cloneState(state: GameState): GameState {
  return structuredClone(state);
}

/**
 * Integrate production from state.lastTick to `to`, assuming constant rates.
 *
 * Production respects storage caps: a full store halts its mine ("Lager voll —
 * Förderung geht verloren"). Only PRODUCTION clamps — deliveries (harvest
 * yields, refunds, plunder) may overfill, and an overfilled store simply
 * produces nothing until spent below the cap. That asymmetry is deliberate:
 * confiscating delivered cargo would punish the player for winning.
 */
function produceSegment(state: GameState, to: number): void {
  const from = state.lastTick;
  if (to <= from) return;
  const hours = (to - from) / 3_600_000;
  const report = productionReport(state, from);
  const caps = storageReport(state);

  const fill = (current: number, perHour: number, cap: number): number =>
    current >= cap ? current : Math.min(cap, current + perHour * hours);

  state.resources.titan = fill(state.resources.titan, report.perHour.titan, caps.titan);
  state.resources.silicate = fill(state.resources.silicate, report.perHour.silicate, caps.silicate);
  state.resources.isotope = fill(state.resources.isotope, report.perHour.isotope, caps.isotope);
  state.lastTick = to;
}

/**
 * Apply a finished job to the state. `planetIndex` identifies the owning
 * planet for building, ship, and defense jobs; research remains global.
 */
function applyCompletion(state: GameState, job: QueueJob, at: number, planetIndex: number): EngineEvent {
  switch (job.kind) {
    case 'building': {
      const key = job.key as keyof typeof BUILDINGS;
      setBuildingLevel(state, key, job.amount, planetIndex);
      return {
        at, type: 'jobCompleted',
        message: `${BUILDINGS[key].label} Stufe ${job.amount} fertiggestellt.`,
        data: { kind: 'building', key, level: job.amount },
      };
    }
    case 'research': {
      const key = job.key as keyof typeof RESEARCH;
      state.research[key] = job.amount;
      return {
        at, type: 'jobCompleted',
        message: `${RESEARCH[key].label} Stufe ${job.amount} erforscht.`,
        data: { kind: 'research', key, level: job.amount },
      };
    }
    case 'ship': {
      const key = job.key as ShipKey;
      state.planets[planetIndex]!.ships[key] += job.amount;
      return {
        at, type: 'jobCompleted',
        message: `${job.amount}× ${UNITS[key].label} vom Stapel gelaufen.`,
        data: { kind: 'ship', key, count: job.amount },
      };
    }
    case 'defense': {
      const key = job.key as DefenseKey;
      state.planets[planetIndex]!.defense[key] += job.amount;
      return {
        at, type: 'jobCompleted',
        message: `${job.amount}× ${UNITS[key].label} errichtet.`,
        data: { kind: 'defense', key, count: job.amount },
      };
    }
  }
}

/** Start the next waiting job in a sequential queue, if the slot is free. */
function promoteQueue(queue: QueueJob[], at: number): void {
  const head = queue[0];
  if (!head || head.startedAt !== 0) return;
  // Duration was priced at enqueue time and stored in `amount`-agnostic form:
  // finishesAt was precomputed as duration; here we anchor it to `at`.
  const duration = head.finishesAt;
  head.startedAt = at;
  head.finishesAt = at + duration;
}

/**
 * Spawn a sector event when its window opens.
 *
 * GDD §3 fixes the asteroid/comet split at 70/30. Pirate raids are the third
 * event type (GDD §3 "kleine NPC-Piraten"); they take a slice of the roll and
 * only appear once the colony is worth raiding, so the opening hours stay
 * about building rather than defending.
 */
function maybeSpawnEvent(state: GameState, at: number): EngineEvent | null {
  if (state.event || at < state.nextEventAt) return null;
  const spawnAt = state.nextEventAt;

  const typeRoll = nextRandom(state.rngSeed);
  state.rngSeed = typeRoll.seed;
  const talents = talentEffects(state.talents);
  const cometShare = Math.min(
    0.9,
    0.3 * (1 + (state.research.astrometrics * 0.05 + talents.cometChanceBonus)),
  );
  // Raids join the rotation from level 3 onwards, taking 20 % of the roll.
  const raidShare = state.level >= 3 ? 0.2 : 0;

  const id = state.nextId++;
  let message: string;

  if (typeRoll.value < raidShare) {
    const budget = defenderPower(state) * RAID_POWER_RATIO;
    const fleet = rollPirateFleet(state, budget);
    const ships = Object.values(fleet).reduce((a, b) => a + (b ?? 0), 0);
    state.event = {
      id,
      type: 'raid',
      spawnedAt: spawnAt,
      deadline: spawnAt + raidLeadTimeMs(state),
      size: budget,
      resolved: false,
      fleet,
    };
    message = `Piraten-Überfall entdeckt! ${ships} feindliche Einheiten im Anflug.`;
  } else if (typeRoll.value < raidShare + cometShare) {
    // Impact 2 h after detection — the window the phalanx warning gives the
    // player to intercept, shield or accept the hit (GDD §3).
    state.event = {
      id, type: 'comet', spawnedAt: spawnAt,
      deadline: spawnAt + 2 * 3_600_000, size: 0, resolved: false,
    };
    message = 'Warnung: Komet auf Kollisionskurs! Einschlag in 2 Stunden.';
  } else {
    const sizeRoll = nextInt(state.rngSeed, 20_000, 80_000);
    state.rngSeed = sizeRoll.seed;
    // Ore asteroid dissolves after 4 h (GDD §3).
    state.event = {
      id, type: 'asteroid', spawnedAt: spawnAt,
      deadline: spawnAt + 4 * 3_600_000, size: sizeRoll.value, resolved: false,
    };
    message = 'Ein ertragreicher Erz-Asteroid ist im Orbit erschienen.';
  }

  return {
    at: spawnAt,
    type: 'eventSpawned',
    message,
    data: { eventType: state.event.type, id },
  };
}

/**
 * Resolve an arriving pirate raid against the colony's defences.
 *
 * Losses are applied to whatever actually died, debris from BOTH sides lands
 * in orbit for the Erz-Sammelschiffe, and a pirate victory plunders within the
 * GDD's 50 % cap. Defeated defences are partially rebuilt by the astronomy
 * talent band (GDD §2 tier 11–15).
 */
function resolveRaid(state: GameState, at: number): EngineEvent[] {
  const event = state.event!;
  const attacker = { units: event.fleet ?? {} };
  const battle = simulateBattle(attacker, defenderParty(state), state.rngSeed);
  // Advance the seed past the battle so the next roll differs.
  const advance = nextRandom(state.rngSeed);
  state.rngSeed = advance.seed;

  const talents = talentEffects(state.talents);

  // Apply defender losses, then the talent-driven repair quota.
  for (const [key, lost] of Object.entries(battle.defenderLosses) as [UnitKey, number][]) {
    const repaired = Math.floor(lost * talents.defenseRepairRate);
    const net = lost - repaired;
    if (DEFENSE_KEYS.includes(key as never)) {
      const k = key as DefenseKey;
      state.planets[0]!.defense[k] = Math.max(0, state.planets[0]!.defense[k] - net);
    } else {
      const k = key as ShipKey;
      state.planets[0]!.ships[k] = Math.max(0, state.planets[0]!.ships[k] - net);
    }
  }

  state.debris.titan += battle.debris.titan;
  state.debris.silicate += battle.debris.silicate;

  if (battle.winner === 'defender' || battle.winner === 'draw') {
    const xp = Math.round(event.size / 50);
    // grantXp may cascade level-ups — they are returned alongside, never
    // stashed in module state (the engine must stay free of shared mutables).
    const levelUps = grantXp(state, xp, at);
    return [
      {
        at,
        type: 'raidRepelled',
        message: `Piraten-Überfall abgewehrt! +${xp} XP, Trümmerfeld im Orbit.`,
        data: { debris: battle.debris, losses: battle.defenderLosses },
      },
      ...levelUps,
    ];
  }

  const plunder = plunderAmounts(state, state.rngSeed);
  state.rngSeed = plunder.seed;
  state.resources.titan -= plunder.taken.titan;
  state.resources.silicate -= plunder.taken.silicate;
  state.resources.isotope -= plunder.taken.isotope;

  return [
    {
      at,
      type: 'raidLost',
      message:
        `Die Verteidigung ist gefallen. Geplündert: ${Math.round(plunder.taken.titan)} Titan, ` +
        `${Math.round(plunder.taken.silicate)} Silikat, ${Math.round(plunder.taken.isotope)} Isotop.`,
      data: { plundered: plunder.taken, losses: battle.defenderLosses },
    },
  ];
}

/** Schedule the next event window: 4–8 h out (GDD §3). */
function scheduleNextEvent(state: GameState, from: number): void {
  const roll = nextInt(state.rngSeed, 4 * 60, 8 * 60);
  state.rngSeed = roll.seed;
  state.nextEventAt = from + roll.value * 60_000;
}

/** Unresolved comet hits: damage 1–2 buildings, leave a silicate crater (GDD §3). */
function applyCometImpact(state: GameState, at: number): EngineEvent {
  const built = (Object.entries(state.planets[0]!.buildings) as [BuildingKey, number][])
    .filter(([key, lvl]) => lvl > 0 && !state.planets[0]!.damaged.includes(key))
    .map(([key]) => key);

  const countRoll = nextInt(state.rngSeed, 1, 2);
  state.rngSeed = countRoll.seed;
  const toDamage = Math.min(countRoll.value, built.length);

  const hit: string[] = [];
  for (let i = 0; i < toDamage; i++) {
    const pick = nextInt(state.rngSeed, 0, built.length - 1);
    state.rngSeed = pick.seed;
    const key = built.splice(pick.value, 1)[0]!;
    state.planets[0]!.damaged.push(key);
    hit.push(BUILDINGS[key].label);
  }

  // Compensation: 24 h of doubled silicate output from the crater (GDD §3).
  state.planets[0]!.crater = { factor: 2, expiresAt: at + 24 * 3_600_000 };

  return {
    at,
    type: 'cometImpact',
    message: hit.length > 0
      ? `Kometen-Einschlag! Beschädigt: ${hit.join(', ')}. Der Krater liefert 24 h lang Kristall-Silikat.`
      : 'Kometen-Einschlag im Ödland — der Krater liefert 24 h lang Kristall-Silikat.',
    data: { damaged: hit },
  };
}

/**
 * Resolve an attack on a pirate camp: battle, losses, cargo-capped loot.
 * The fleet then flies home; delivery happens at `returnsAt`.
 */
function resolveExpedition(state: GameState, mission: Extract<GameState['fleetMissions'][number], { kind: 'campAttack' }>, at: number): EngineEvent[] {
  const camp = state.camps.find((c) => c.id === mission.campId);
  const talents = talentEffects(state.talents);

  const battle = simulateBattle(
    raidingParty(state, mission.fleet, talents.capitalDamageBonus),
    { units: camp?.garrison ?? {} },
    state.rngSeed,
  );
  const advance = nextRandom(state.rngSeed);
  state.rngSeed = advance.seed;

  mission.resolved = true;
  mission.losses = battle.attackerLosses;
  mission.won = battle.winner === 'attacker';

  // Surviving hulls define how much loot can actually be carried home.
  const survivors: Partial<Record<UnitKey, number>> = {};
  for (const [key, count] of Object.entries(mission.fleet) as [UnitKey, number][]) {
    const left = count - (battle.attackerLosses[key] ?? 0);
    if (left > 0) survivors[key] = left;
  }

  const events: EngineEvent[] = [];
  if (mission.won && camp) {
    const capacity = fleetCargo(state, survivors);
    const total = camp.loot.titan + camp.loot.silicate + camp.loot.isotope;
    const ratio = total > 0 ? Math.min(1, capacity / total) : 0;
    mission.loot = {
      titan: Math.floor(camp.loot.titan * ratio),
      silicate: Math.floor(camp.loot.silicate * ratio),
      isotope: Math.floor(camp.loot.isotope * ratio),
    };
    // The camp is cleared out.
    state.camps = state.camps.filter((c) => c.id !== camp.id);
    state.debris.titan += battle.debris.titan;
    state.debris.silicate += battle.debris.silicate;
    events.push({
      at,
      type: 'expeditionResolved',
      message: `${mission.campName} überrannt! Beute wird verladen.`,
      data: { loot: mission.loot, losses: battle.attackerLosses },
    });
    events.push(...grantXp(state, 120 + camp.tier * 90, at));
  } else {
    mission.loot = {};
    state.debris.titan += battle.debris.titan;
    state.debris.silicate += battle.debris.silicate;
    events.push({
      at,
      type: 'expeditionResolved',
      message: `Angriff auf ${mission.campName} gescheitert. Die Reste treten den Rückflug an.`,
      data: { losses: battle.attackerLosses },
    });
  }
  return events;
}

/**
 * Grade the onboarding chain against the current state and pay out.
 *
 * Objectives are questions about state rather than counters, so this can run
 * at any time and cascade through several missions at once — which is exactly
 * what happens when a player returns from a long absence with a full queue.
 */
function gradeMissions(state: GameState, at: number): EngineEvent[] {
  const events: EngineEvent[] = [];
  // Bounded loop: the chain is finite and each completion advances the index.
  for (let guard = 0; guard < MISSIONS.length + 1; guard++) {
    const mission = activeMission(state);
    if (!mission || !mission.isComplete(state)) break;

    const reward = mission.reward;
    if (reward.resources) {
      for (const [key, amount] of Object.entries(reward.resources) as [
        keyof typeof state.resources,
        number,
      ][]) {
        state.resources[key] += amount;
      }
    }
    for (const [key, count] of Object.entries(reward.ships ?? {})) {
      state.planets[0]!.ships[key as ShipKey] += count as number;
    }
    for (const [key, count] of Object.entries(reward.defense ?? {})) {
      state.planets[0]!.defense[key as DefenseKey] += count as number;
    }

    state.missionIndex += 1;
    events.push({
      at,
      type: 'missionCompleted',
      message: `Mission abgeschlossen: ${mission.title}`,
      data: { id: mission.id, reward },
    });
    if (reward.xp) events.push(...grantXp(state, reward.xp, at));
  }
  return events;
}

/** The next timestamp at which rates or state change, or null. */
function nextBoundary(state: GameState, now: number): number | null {
  const candidates: number[] = [];
  const queues: QueueJob[][] = [
    state.researchQueue,
    ...state.planets.map((p) => p.shipyardQueue),
    ...state.planets.map((p) => p.buildQueue),
  ];
  for (const queue of queues) {
    const head = queue[0];
    if (head && head.startedAt !== 0 && head.finishesAt <= now) candidates.push(head.finishesAt);
  }
  if (state.event && !state.event.resolved && state.event.deadline <= now) candidates.push(state.event.deadline);
  if (!state.event && state.nextEventAt <= now) candidates.push(state.nextEventAt);
  for (const mission of state.fleetMissions) {
    if (mission.kind === 'scoutCamp') {
      if (mission.arrivesAt <= now) candidates.push(mission.arrivesAt);
      continue;
    }
    if (mission.kind === 'scoutPlayer') {
      if (!mission.resolutionRequested && mission.arrivesAt <= now) candidates.push(mission.arrivesAt);
      if (mission.resolved && mission.returnsAt <= now) candidates.push(mission.returnsAt);
      continue;
    }
    if (mission.kind === 'playerAttack') {
      if (!mission.resolutionRequested && mission.arrivesAt <= now) candidates.push(mission.arrivesAt);
      if (mission.resolved && mission.returnsAt <= now) candidates.push(mission.returnsAt);
      continue;
    }
    if (mission.kind === 'campAttack' && !mission.resolved && mission.arrivesAt <= now) candidates.push(mission.arrivesAt);
    if (mission.returnsAt <= now) candidates.push(mission.returnsAt);
  }
  for (const planet of state.planets) {
    if (planet.crater && planet.crater.expiresAt <= now) candidates.push(planet.crater.expiresAt);
  }
  if (marketUnlocked(state) && state.market.refreshAt <= now) candidates.push(state.market.refreshAt);
  if (state.campsRefreshAt <= now) candidates.push(state.campsRefreshAt);
  // Timed buffs change every rate in the game, so their edges are boundaries too.
  const effectAt = nextEffectBoundary(state, state.lastTick);
  if (effectAt !== null && effectAt <= now) candidates.push(effectAt);
  if (candidates.length === 0) return null;
  return Math.min(...candidates);
}

/** Handle everything that is due exactly at `t` (rates already integrated to t). */
function fireDue(state: GameState, t: number, events: EngineEvent[]): void {
  // Job completions. Build and shipyard queues run independently per planet;
  // research is the only queue that stays global.
  for (let i = 0; i < state.planets.length; i++) {
    const queue = state.planets[i]!.buildQueue;
    const head = queue[0];
    if (head && head.startedAt !== 0 && head.finishesAt <= t) {
      queue.shift();
      events.push(applyCompletion(state, head, t, i));
      promoteQueue(queue, t);
    }
  }
  for (let i = 0; i < state.planets.length; i++) {
    const queue = state.planets[i]!.shipyardQueue;
    const head = queue[0];
    if (head && head.startedAt !== 0 && head.finishesAt <= t) {
      queue.shift();
      events.push(applyCompletion(state, head, t, i));
      promoteQueue(queue, t);
    }
  }
  {
    const queue = state.researchQueue;
    const head = queue[0];
    if (head && head.startedAt !== 0 && head.finishesAt <= t) {
      queue.shift();
      events.push(applyCompletion(state, head, t, 0));
      promoteQueue(queue, t);
    }
  }
  // Crater expiry (nothing to announce — rates simply change).
  for (const planet of state.planets) {
    if (planet.crater && planet.crater.expiresAt <= t) planet.crater = null;
  }
  // Harvest and expedition returns are handled below in one mission loop.
  // Pirate camp board rotation.
  if (t >= state.campsRefreshAt) {
    refreshCamps(state, t);
    events.push({
      at: t,
      type: 'campsRefreshed',
      message: 'Die Sensoren haben neue Piratenlager im Heimatsystem erfasst.',
    });
  }

  // Expedition: battle on arrival, delivery on return. All missions are
  // independent, so concurrent fleets can resolve at the same boundary.
  for (let i = state.fleetMissions.length - 1; i >= 0; i--) {
    const mission = state.fleetMissions[i]!;
    if (mission.kind === 'scoutCamp') {
      if (mission.arrivesAt > t) continue;
      state.fleetMissions.splice(i, 1);
      const camp = state.camps.find((entry) => entry.id === mission.campId);
      if (!camp || camp.scouted) continue;
      const roll = nextRandom(state.rngSeed);
      state.rngSeed = roll.seed;
      if (roll.value < scoutSuccessChance(state, camp, mission.probes)) {
        camp.scouted = true;
        events.push({ at: t, type: 'scoutReport', message: `Spionagebericht ${camp.name}: Garnison und Beute erfasst.`, data: { campId: mission.campId, garrison: camp.garrison, loot: camp.loot } });
      } else {
        events.push({ at: t, type: 'scoutLost', message: `Die Sonden wurden über ${camp.name} entdeckt und zerstört.`, data: { campId: mission.campId, probes: mission.probes } });
      }
      continue;
    }
    if (mission.kind === 'scoutPlayer') {
      if (!mission.resolutionRequested && mission.arrivesAt <= t) {
        mission.resolutionRequested = true;
        events.push({ at: t, type: 'scoutArrival', message: 'Spähsonden am Spielerziel eingetroffen; Auflösung wird verarbeitet.', data: { missionId: mission.id, target: mission.target } });
      }
      if (mission.resolved && mission.returnsAt <= t) {
        state.fleetMissions.splice(i, 1);
        // A detected attempt destroys the probes (GDD §5) — nothing flies
        // home. Every other outcome (success, or the target vanishing) is a
        // real round trip, same as any other mission.
        if (mission.success !== false) {
          state.planets[0]!.ships.probe += mission.probes;
          events.push({ at: t, type: 'scoutReturned', message: 'Spähsonden zurückgekehrt.', data: { target: mission.target } });
        }
      }
      continue;
    }
    if (mission.kind === 'playerAttack') {
      if (!mission.resolutionRequested && mission.arrivesAt <= t) {
        mission.resolutionRequested = true;
        events.push({ at: t, type: 'pvpArrival', message: 'Angriffsflotte am Ziel eingetroffen; Auflösung wird verarbeitet.', data: { missionId: mission.id, target: mission.target } });
      }
      if (!mission.resolved || mission.returnsAt > t) continue;
      state.fleetMissions.splice(i, 1);
      // Survivors and loot only arrive home after the full round trip — same
      // delivery shape as campAttack, so PvP can't cycle hangar capacity
      // faster than any other mission type.
      for (const [key, count] of Object.entries(mission.fleet) as [ShipKey, number][]) {
        const lost = mission.losses?.[key] ?? 0;
        const survivors = Math.max(0, count - lost);
        if (survivors > 0) state.planets[0]!.ships[key] += survivors;
      }
      if (mission.loot) {
        state.resources.titan += mission.loot.titan ?? 0;
        state.resources.silicate += mission.loot.silicate ?? 0;
        state.resources.isotope += mission.loot.isotope ?? 0;
      }
      events.push({
        at: t,
        type: 'expeditionReturned',
        message: mission.won === 'attacker' ? 'PvP-Flotte zurück — Beute eingelagert.' : 'Reste der PvP-Flotte zurückgekehrt.',
        data: { loot: mission.loot, losses: mission.losses, target: mission.target },
      });
      continue;
    }
    if (mission.kind === 'campAttack' && !mission.resolved && mission.arrivesAt <= t) {
      events.push(...resolveExpedition(state, mission, t));
    }
    if (mission.returnsAt > t) continue;
    state.fleetMissions.splice(i, 1);
    if (mission.kind === 'harvestAsteroid' || mission.kind === 'harvestDebris') {
      state.planets[0]!.ships.minerShip += mission.ships;
      state.resources.titan += mission.yield.titan ?? 0;
      state.resources.silicate += mission.yield.silicate ?? 0;
      events.push({ at: t, type: 'harvestReturned', message: `Sammelflotte zurückgekehrt: +${Math.round(mission.yield.titan ?? 0)} Titan, +${Math.round(mission.yield.silicate ?? 0)} Silikat.`, data: { ...mission.yield } });
      continue;
    }
    if (mission.kind !== 'campAttack') continue;
    // Survivors rejoin the hangar.
    for (const [key, count] of Object.entries(mission.fleet) as [UnitKey, number][]) {
      const lost = mission.losses?.[key] ?? 0;
      const survivors = Math.max(0, count - lost);
      if (survivors > 0) state.planets[0]!.ships[key as ShipKey] += survivors;
    }
    if (mission.loot) {
      state.resources.titan += mission.loot.titan ?? 0;
      state.resources.silicate += mission.loot.silicate ?? 0;
      state.resources.isotope += mission.loot.isotope ?? 0;
    }
    events.push({
      at: t,
      type: 'expeditionReturned',
      message: mission.won
        ? `Flotte von ${mission.campName} zurück — Beute eingelagert.`
        : `Reste der Flotte von ${mission.campName} zurückgekehrt.`,
      data: { loot: mission.loot, losses: mission.losses },
    });
  }

  // Market offer board rotation.
  if (t >= state.market.refreshAt && marketUnlocked(state)) {
    refreshOffers(state, t);
    events.push({
      at: t,
      type: 'marketRefreshed',
      message: 'Das Händler-Portal hat neue Angebote.',
    });
  }

  // Event lifecycle.
  if (state.event && !state.event.resolved && state.event.deadline <= t) {
    const event = state.event;
    if (event.type === 'comet') {
      // Offline-safe priority: consume a missile first, then one dome. This
      // mirrors the manual tools while preserving a deliberate intervention
      // window for online players; unattended events never punish ownership
      // of a ready defense stockpile.
      if (state.planets[0]!.defense.interceptor > 0) {
        state.planets[0]!.defense.interceptor -= 1;
        state.event = null;
        events.push({ at: t, type: 'cometResolved', message: 'Komet automatisch abgefangen.', data: { via: 'auto-intercept' } });
      } else if (state.planets[0]!.defense.shieldDome > 0) {
        state.planets[0]!.defense.shieldDome -= 1;
        state.event = null;
        events.push({ at: t, type: 'cometResolved', message: 'Schildkuppel hat den Kometen automatisch absorbiert.', data: { via: 'auto-shield' } });
      } else {
        state.event = null;
        events.push(applyCometImpact(state, t));
      }
    } else if (event.type === 'raid') {
      // resolveRaid reads state.event, so clear it only afterwards.
      events.push(...resolveRaid(state, t));
      state.event = null;
    } else {
      state.event = null;
      events.push({ at: t, type: 'asteroidExpired', message: 'Der Erz-Asteroid hat sich aufgelöst.' });
    }
    scheduleNextEvent(state, t);
  }
  const spawned = maybeSpawnEvent(state, t);
  if (spawned) events.push(spawned);

  for (const effect of pruneEffects(state, t)) {
    events.push({
      at: t,
      type: 'effectExpired',
      message: `${effect.label} ist abgelaufen — ${MODIFIER_LABELS[effect.category]} zurück auf Normalwert.`,
      data: { category: effect.category, percent: effect.percent },
    });
  }

  events.push(...gradeMissions(state, t));
}

/**
 * Advance the world to `now`. Pure: returns a new state, never mutates input.
 */
export function tick(input: GameState, now: number): TickResult {
  const state = cloneState(input);
  const events: EngineEvent[] = [];
  if (now <= state.lastTick) return { state, events };

  // Walk boundary by boundary so every segment integrates with correct rates.
  // Hard iteration cap guards against pathological saves.
  for (let i = 0; i < 10_000; i++) {
    const boundary = nextBoundary(state, now);
    if (boundary === null || boundary > now) break;
    produceSegment(state, Math.max(boundary, state.lastTick));
    fireDue(state, boundary, events);
  }
  produceSegment(state, now);
  for (const effect of pruneEffects(state, now)) {
    events.push({
      at: now,
      type: 'effectExpired',
      message: `${effect.label} ist abgelaufen — ${MODIFIER_LABELS[effect.category]} zurück auf Normalwert.`,
      data: { category: effect.category, percent: effect.percent },
    });
  }
  events.push(...gradeMissions(state, now));

  return { state, events };
}
